import { useEffect, useRef, useState } from "react";

// Smoothly animates a numeric value from its previous render to the new one,
// using requestAnimationFrame easing. Returns the in-progress display value.
// Useful for resource counters / hero shards / quest progress — anything that
// would otherwise snap from 1000 → 1500 with zero feedback.
export function useCountUp(target: number, durationMs = 600): number {
  const [display, setDisplay] = useState(target);
  const prevTargetRef = useRef(target);

  useEffect(() => {
    const from = prevTargetRef.current;
    const to = target;
    if (from === to) return;
    prevTargetRef.current = to;

    const startTime = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const value = Math.round(from + (to - from) * eased);
      setDisplay(value);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return display;
}

// Tracks the previous value of a prop across renders. Used to detect "value
// just went up" so we can fire a one-shot animation (value-pop / floating-N).
export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

// Convenience: returns the positive delta vs last render (or 0).
// Re-renders that don't change the value return 0.
export function useDelta(value: number): number {
  const prev = usePrevious(value);
  if (prev === undefined) return 0;
  return value - prev;
}

// Fires a one-shot animation class on the wrapped node when `trigger` flips
// to a new identity. The class is applied for `durationMs`, then removed so
// it can be retriggered. Useful when you want to attach an animate-* class
// only on the moment of change.
export function useFlashClass(
  trigger: unknown,
  className: string,
  durationMs = 600,
): { className: string } {
  const [active, setActive] = useState(false);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setActive(true);
    const t = setTimeout(() => setActive(false), durationMs);
    return () => clearTimeout(t);
  }, [trigger]);
  return { className: active ? className : "" };
}
